import Aura from '@primeuix/themes/aura';

export const THEME_PRESET = Aura;

export const PRIMARY_COLORS = [
	{ label: 'Emerald', name: 'emerald' },
	{ label: 'Green', name: 'green' },
	{ label: 'Lime', name: 'lime' },
	{ label: 'Red', name: 'red' },
	{ label: 'Orange', name: 'orange' },
	{ label: 'Amber', name: 'amber' },
	{ label: 'Yellow', name: 'yellow' },
	{ label: 'Teal', name: 'teal' },
	{ label: 'Cyan', name: 'cyan' },
	{ label: 'Sky', name: 'sky' },
	{ label: 'Blue', name: 'blue' },
	{ label: 'Indigo', name: 'indigo' },
	{ label: 'Violet', name: 'violet' },
	{ label: 'Purple', name: 'purple' },
	{ label: 'Fuchsia', name: 'fuchsia' },
	{ label: 'Pink', name: 'pink' },
	{ label: 'Rose', name: 'rose' },
	{ label: 'Navy', name: 'navy' },
	{ label: 'Berry', name: 'berry' },
] as const;
export type PrimaryColorName = 'custom' | (typeof PRIMARY_COLORS)[number]['name'];

export const DEFAULT_CUSTOM_PRIMARY_HEX = '#3b82f6';
export const DEFAULT_CUSTOM_SURFACE_HEX = '#71717a';

export const CUSTOM_PALETTES: Record<string, Record<string, string>> = {
	berry: {
		'50': '#fae6f7',
		'100': '#f0bfe9',
		'200': '#de80d3',
		'300': '#c94db8',
		'400': '#ab1a97',
		'500': '#8C007B',
		'600': '#760068',
		'700': '#600054',
		'800': '#4a0041',
		'900': '#34002e',
		'950': '#23001f',
	},
	navy: {
		'50': '#eaf1fb',
		'100': '#c5d9f3',
		'200': '#8db4e6',
		'300': '#558ed9',
		'400': '#2a6ec4',
		'500': '#004994',
		'600': '#003d7d',
		'700': '#003166',
		'800': '#00254f',
		'900': '#001938',
		'950': '#001024',
	},
};

export const SURFACE_COLORS = [
	{ label: 'Slate', name: 'slate' },
	{ label: 'Gray', name: 'gray' },
	{ label: 'Zinc', name: 'zinc' },
	{ label: 'Neutral', name: 'neutral' },
	{ label: 'Stone', name: 'stone' },
] as const;
export type SurfaceColorName = 'custom' | (typeof SURFACE_COLORS)[number]['name'];

export function getPresetPrimitives(): Record<string, Record<string, string>> {
	const raw = THEME_PRESET as Record<string, unknown>;
	const resolved = raw.primitive ? raw : ((raw.default as Record<string, unknown> | undefined) ?? raw);
	const presetPrimitives = (resolved.primitive ?? {}) as Record<string, Record<string, string>>;
	return { ...presetPrimitives, ...CUSTOM_PALETTES };
}

const PRIMARY_COLOR_NAMES = PRIMARY_COLORS.map((c) => c.name) as unknown as PrimaryColorName[];
const SURFACE_COLOR_NAMES = SURFACE_COLORS.map((c) => c.name) as unknown as SurfaceColorName[];

export function resolvePrimaryColorName(stored: null | string): PrimaryColorName {
	if (!stored) {
		return 'navy';
	}
	if (stored === 'custom') {
		return 'custom';
	}
	return PRIMARY_COLOR_NAMES.includes(stored as PrimaryColorName) ? (stored as PrimaryColorName) : 'blue';
}

export function resolveSurfaceColorName(stored: null | string): SurfaceColorName {
	if (!stored) {
		return 'zinc';
	}
	if (stored === 'custom') {
		return 'custom';
	}
	return SURFACE_COLOR_NAMES.includes(stored as SurfaceColorName) ? (stored as SurfaceColorName) : 'zinc';
}

const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

function isValidHexColor(value: unknown): value is string {
	return typeof value === 'string' && HEX_COLOR_PATTERN.test(value);
}

export function resolveStoredHexColor(stored: null | string, fallback: string): string {
	return isValidHexColor(stored) ? stored.toLowerCase() : fallback;
}

interface PaletteStop {
	mixColor: '#000000' | '#ffffff';
	mixRatio: number;
	stop: string;
}

const PALETTE_STOPS: PaletteStop[] = [
	{ mixColor: '#ffffff', mixRatio: 0.95, stop: '50' },
	{ mixColor: '#ffffff', mixRatio: 0.86, stop: '100' },
	{ mixColor: '#ffffff', mixRatio: 0.7, stop: '200' },
	{ mixColor: '#ffffff', mixRatio: 0.5, stop: '300' },
	{ mixColor: '#ffffff', mixRatio: 0.25, stop: '400' },
	{ mixColor: '#ffffff', mixRatio: 0, stop: '500' },
	{ mixColor: '#000000', mixRatio: 0.12, stop: '600' },
	{ mixColor: '#000000', mixRatio: 0.25, stop: '700' },
	{ mixColor: '#000000', mixRatio: 0.4, stop: '800' },
	{ mixColor: '#000000', mixRatio: 0.55, stop: '900' },
	{ mixColor: '#000000', mixRatio: 0.7, stop: '950' },
];

function hexToRgb(hex: string): { b: number; g: number; r: number } {
	const cleaned = hex.replace('#', '');
	return {
		b: parseInt(cleaned.substring(4, 6), 16),
		g: parseInt(cleaned.substring(2, 4), 16),
		r: parseInt(cleaned.substring(0, 2), 16),
	};
}

function rgbToHex(r: number, g: number, b: number): string {
	const toHex = (channel: number): string => Math.round(channel).toString(16).padStart(2, '0');
	return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function mixHex(baseHex: string, targetHex: string, mixRatio: number): string {
	const base = hexToRgb(baseHex);
	const target = hexToRgb(targetHex);
	const r = base.r * (1 - mixRatio) + target.r * mixRatio;
	const g = base.g * (1 - mixRatio) + target.g * mixRatio;
	const b = base.b * (1 - mixRatio) + target.b * mixRatio;
	return rgbToHex(r, g, b);
}

export function generatePaletteFromHex(hex: string): Record<string, string> {
	const palette: Record<string, string> = {};
	for (const { mixColor, mixRatio, stop } of PALETTE_STOPS) {
		palette[stop] = mixRatio === 0 ? hex.toLowerCase() : mixHex(hex, mixColor, mixRatio);
	}
	return palette;
}
